import React, { memo } from 'react'
import { styled } from '@mui/system'
import CloseIcon from '@mui/icons-material/Close'
import { colorPalette } from '../../style/colorPalette'

const BannerContainer = styled('div')({
  backgroundColor: colorPalette.skyBlue[0],
  borderBottom: `1px solid ${colorPalette.skyBlue[2]}`,
  padding: '12px 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  position: 'relative',
  zIndex: 1200,
})

const ContentArea = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
})

const SingleLineContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
})

const Title = styled('p')({
  margin: 0,
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: 1.5,
  color: colorPalette.skyBlue[8],
})

const Description = styled('p')({
  margin: 0,
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: 1.5,
  color: colorPalette.monotone[4],
})

const Link = styled('a')({
  fontSize: '12px',
  color: colorPalette.monotone[4],
  textDecoration: 'underline',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  '&:hover': {
    opacity: 0.8,
  },
})

const ActionsArea = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const CloseButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px',
  border: 'none',
  borderRadius: '2px',
  backgroundColor: colorPalette.monotone[3],
  cursor: 'pointer',
  transition: 'opacity 0.2s',
  '&:hover': {
    opacity: 0.8,
  },
  '& svg': {
    fontSize: '12px',
    color: colorPalette.monotone[0],
  },
})

type Props = {
  /** メインメッセージ（タイトル） */
  title: string
  /** サブメッセージ（説明文） - 省略時は1行表示 */
  description?: string
  /** リンクテキスト */
  linkText?: string
  /** リンククリック時のハンドラ */
  onLinkClick?: () => void
  /** 閉じるボタンクリック時のハンドラ */
  onClose?: () => void
  /** 閉じるボタンを非表示にする */
  hideCloseButton?: boolean
}

export const AnnouncementBanner: React.FC<Props> = memo(
  ({
    title,
    description,
    linkText,
    onLinkClick,
    onClose,
    hideCloseButton = false,
  }) => {
    const isSingleLine = !description

    return (
      <BannerContainer>
        <ContentArea>
          {isSingleLine ? (
            <SingleLineContent>
              <Title>{title}</Title>
              {linkText && <Link onClick={onLinkClick}>{linkText}</Link>}
            </SingleLineContent>
          ) : (
            <>
              <Title>{title}</Title>
              <Description>{description}</Description>
            </>
          )}
        </ContentArea>

        <ActionsArea>
          {!isSingleLine && linkText && (
            <Link onClick={onLinkClick}>{linkText}</Link>
          )}
          {!hideCloseButton && onClose && (
            <CloseButton onClick={onClose}>
              <CloseIcon />
            </CloseButton>
          )}
        </ActionsArea>
      </BannerContainer>
    )
  }
)

AnnouncementBanner.displayName = 'AnnouncementBanner'
